import type { User } from '@/@types/user'
import { getFollowerListQueryOptions, getFollowingListQueryOptions } from '@/utils/fetchers/user'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { getThirdPersonPronoun } from '@/utils/thirdPersonPronoun'
import { css } from '@emotion/react'
import { usePrefetchInfiniteQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash-es'
import { motion } from 'motion/react'
import { type HTMLAttributes, useState } from 'react'
import Avatar from './Avatar'
import FollowToggleButton from './FollowToggleButton'
import ScreenName from './ScreenName'
import UserListModal from './UserListModal'
import UserProfileTag from './UserProfileTag'

export interface UserProfileProps extends HTMLAttributes<HTMLDivElement> {
	data: User
}

export default function UserProfile({ data, ...props }: UserProfileProps) {
	const [isFollowingListModalShow, setIsFollowingListModalShow] = useState(false)
	const [isFollowerListModalShow, setIsFollowerListModalShow] = useState(false)

	const currentUser = useCurrentUser()
	const isCurrentUser = currentUser.id === data.id

	usePrefetchInfiniteQuery(getFollowingListQueryOptions(data.username))
	usePrefetchInfiniteQuery(getFollowerListQueryOptions(data.username))

	if (!data) {
		return null
	}

	const {
		backgroundImage,
		backgroundMaskColor,
		avatarImage,
		zodiac,
		screenName,
		industry,
		bio,
		statsCount,
		isSponsor,
		decorations,
		gender,
		city,
		profileTags = [],
	} = data
	const hasBackgroundImage = !!backgroundImage?.picUrl

	return (
		<section
			css={css`
				position: relative;
			`}
			{...props}
		>
			{/* background image */}
			<div
				css={css`
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					z-index: 0;
					width: 100%;
					height: 260px;
					overflow: hidden;
				`}
			>
				<motion.img
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					loading="lazy"
					src={backgroundImage?.picUrl ?? avatarImage.picUrl}
					css={css`
						height: 100%;
						width: 100%;
						object-fit: cover;
						${!hasBackgroundImage &&
						css`
							filter: blur(43px);
						`}
					`}
				/>
			</div>

			<div
				css={css`
					padding-top: 160px;
					display: flex;
					flex-direction: column;
					gap: 20px;
				`}
			>
				<section>
					<Avatar
						data={data}
						avatarKey="picUrl"
						css={css`
							height: 158px;
							width: fit-content;
							margin-left: 20px;

							.jk-avatar {
								height: 158px;
								width: 158px;
								border: 4px solid var(--bg-body-1);
							}

							.sponsor-icon {
								width: 48px;
								height: 48px;
							}
						`}
					/>

					<section
						css={css`
							display: grid;
							grid-gap: 12px;
							margin-top: 20px;
						`}
					>
						<div
							css={css`
								display: flex;
								justify-content: space-between;
								align-items: center;

								> * {
									flex-shrink: 0;
								}
							`}
						>
							<ScreenName
								data={data}
								css={css`
									font-size: 36px;
									font-weight: 500;
									margin: 0;
								`}
							/>
							<FollowToggleButton data={data} />
						</div>

						<section
							css={css`
								display: flex;
								gap: 28px;
								align-items: center;

								.stat {
									display: flex;
									align-items: center;
									gap: 6px;

									&-count {
										font-size: 20px;
										font-weight: 500;
										line-height: 28px;
									}

									&-label {
										color: var(--tint-secondary, #808080);
										font-size: 20px;
										font-weight: 400;
										line-height: 28px;
									}
								}
							`}
						>
							<div className="stat">
								<span
									className="stat-count hover-underline"
									onClick={() => {
										setIsFollowingListModalShow(true)
									}}
									aria-label="关注数"
								>
									{statsCount.followingCount}
								</span>
								<span className="stat-label">关注</span>
							</div>
							<div className="stat">
								<span
									className="stat-count hover-underline"
									onClick={() => {
										setIsFollowerListModalShow(true)
									}}
									aria-label="被关注数"
								>
									{statsCount.followedCount}
								</span>
								<span className="stat-label">被关注</span>
							</div>
							<div className="stat">
								<span className="stat-count">{statsCount.respectedCount}</span>
								<span className="stat-label">夸夸</span>
							</div>
						</section>

						{/* member */}
						{isSponsor && decorations?.sponsor?.picUrl && (
							<section
								css={css`
									display: flex;
									gap: 6px;
									align-items: center;
								`}
							>
								<img
									width={24}
									height={24}
									loading="lazy"
									referrerPolicy="no-referrer"
									src={decorations.sponsor?.picUrl}
									css={css`
										width: 24px;
										height: 24px;
									`}
								/>
								<span>Jike Yellow</span>
							</section>
						)}

						{!isEmpty(bio) && (
							<section
								css={css`
									font-size: 16px;
									line-height: 24px;
									white-space: pre-wrap;
								`}
							>
								{bio}
							</section>
						)}

						{!isEmpty(profileTags) && (
							<section
								css={css`
									display: flex;
									gap: 6px;
									align-items: center;
									flex-wrap: wrap;
								`}
							>
								{profileTags.map((item) => (
									<UserProfileTag data={item} key={JSON.stringify(item)} />
								))}
							</section>
						)}
					</section>
				</section>
			</div>

			<UserListModal
				title={isCurrentUser ? '我关注的人' : `${getThirdPersonPronoun(data.gender)}关注的人`}
				isShow={isFollowingListModalShow}
				onShowChange={setIsFollowingListModalShow}
				username={data.username}
				type="following"
			/>
			<UserListModal
				title={isCurrentUser ? '关注我的人' : `关注${getThirdPersonPronoun(data.gender)}的人`}
				isShow={isFollowerListModalShow}
				onShowChange={setIsFollowerListModalShow}
				username={data.username}
				type="follower"
			/>
		</section>
	)
}
